/**
 * Settings Store — manages user settings.
 */

import { create } from 'zustand';
import { Alert } from 'react-native';
import * as api from '../api/client';
import { useAuthStore } from './authStore';
import { useRemindersStore } from './reminderStore';
import i18n, { changeLanguage as changeUiLanguage } from '../i18n';

interface GoogleSyncCounts { synced: number; total: number; unsynced: number; }

interface SettingsState {
  isUpdating: boolean;
  googleConnected: boolean;
  googleEmail: string | null;
  googleSyncCounts: GoogleSyncCounts | null;

  // Actions
  changeUiLanguage: (lang: string) => Promise<void>;
  changeServerLanguage: (lang: string) => Promise<void>;
  changeTimezone: (tz: string) => Promise<void>;
  detectTimezone: (lat: number, lng: number) => Promise<{ tz: string; localTime: string }>;
  toggleAutosync: (enabled: boolean) => Promise<void>;
  toggleHeadsup: (enabled: boolean) => Promise<void>;
  setHeadsupMinutes: (minutes: number) => Promise<void>;
  toggleAutoDoneOnFire: (enabled: boolean) => Promise<void>;
  toggleFollowup: (enabled: boolean) => Promise<void>;
  setFollowupMinutes: (minutes: number) => Promise<void>;
  checkGoogleStatus: () => Promise<void>;
  getGoogleAuthUrl: () => Promise<string>;
  disconnectGoogle: (events?: 'keep' | 'remove') => Promise<void>;
  syncAllGoogle: () => Promise<number>;
  importGoogleEvents: () => Promise<{ imported: number; updated: number }>;
  refreshGoogleSyncCounts: () => Promise<void>;
  updateDisplayName: (name: string) => Promise<void>;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  isUpdating: false,
  googleConnected: false,
  googleEmail: null,
  googleSyncCounts: null,

  changeUiLanguage: async (lang: string) => {
    set({ isUpdating: true });
    try {
      await changeUiLanguage(lang);
    } catch (err) {
      console.warn('[Settings] changeUiLanguage failed:', err);
      Alert.alert(i18n.t('auth.error'), i18n.t('settings.uiLanguageChangeFailed'));
    }
    set({ isUpdating: false });
  },

  changeServerLanguage: async (lang: string) => {
    set({ isUpdating: true });
    try {
      const { user } = await api.updateProfile({ lang });
      useAuthStore.getState().updateUser(user);
    } catch (err) {
      console.warn('[Settings] changeServerLanguage failed:', err);
      Alert.alert(i18n.t('auth.error'), i18n.t('settings.serverLanguageChangeFailed'));
    }
    set({ isUpdating: false });
  },

  changeTimezone: async (tz: string) => {
    set({ isUpdating: true });
    try {
      const { user } = await api.setTimezone(tz);
      useAuthStore.getState().updateUser(user);
    } catch (err) {
      console.warn('[Settings] changeTimezone failed:', err);
      Alert.alert(i18n.t('auth.error'), i18n.t('settings.timezoneChangeFailed'));
    }
    set({ isUpdating: false });
  },

  detectTimezone: async (lat: number, lng: number) => {
    const result = await api.detectTimezone(lat, lng);
    return result;
  },

  toggleAutosync: async (enabled: boolean) => {
    set({ isUpdating: true });
    try {
      const { user } = await api.updateSettings({ autosyncGoogle: enabled });
      useAuthStore.getState().updateUser(user);
    } catch (err) {
      console.warn('[Settings] toggleAutosync failed:', err);
      Alert.alert(i18n.t('auth.error'), i18n.t('settings.autoSyncChangeFailed'));
    }
    set({ isUpdating: false });
  },

  toggleHeadsup: async (enabled: boolean) => {
    set({ isUpdating: true });
    try {
      await api.updatePreferences({ headsup_enabled: enabled });
      const user = useAuthStore.getState().user;
      if (user) {
        useAuthStore.getState().updateUser({ ...user, headsupEnabled: enabled });
      }
      // Reschedule notifications with new preference
      useRemindersStore.getState().rescheduleFromLocal();
    } catch (err) {
      console.warn('[Settings] toggleHeadsup failed:', err);
      Alert.alert(i18n.t('auth.error'), 'Failed to update heads-up setting');
    }
    set({ isUpdating: false });
  },

  setHeadsupMinutes: async (minutes: number) => {
    set({ isUpdating: true });
    try {
      await api.updatePreferences({ headsup_minutes: minutes });
      const user = useAuthStore.getState().user;
      if (user) {
        useAuthStore.getState().updateUser({ ...user, headsupMinutes: minutes });
      }
      useRemindersStore.getState().rescheduleFromLocal();
    } catch (err) {
      console.warn('[Settings] setHeadsupMinutes failed:', err);
      Alert.alert(i18n.t('auth.error'), 'Failed to update heads-up minutes');
    }
    set({ isUpdating: false });
  },

  toggleAutoDoneOnFire: async (enabled: boolean) => {
    set({ isUpdating: true });
    try {
      await api.updatePreferences({ auto_done_on_fire: enabled ? 1 : 0 });
    } catch (err) {
      console.warn('[Settings] toggleAutoDoneOnFire failed:', err);
      Alert.alert(i18n.t('auth.error'), 'Failed to update auto-done setting');
    }
    set({ isUpdating: false });
  },

  toggleFollowup: async (enabled: boolean) => {
    set({ isUpdating: true });
    try {
      await api.updatePreferences({ followup_enabled: enabled ? 1 : 0 });
    } catch (err) {
      console.warn('[Settings] toggleFollowup failed:', err);
      Alert.alert(i18n.t('auth.error'), 'Failed to update follow-up setting');
    }
    set({ isUpdating: false });
  },

  setFollowupMinutes: async (minutes: number) => {
    set({ isUpdating: true });
    try {
      await api.updatePreferences({ followup_interval_minutes: minutes });
    } catch (err) {
      console.warn('[Settings] setFollowupMinutes failed:', err);
      Alert.alert(i18n.t('auth.error'), 'Failed to update follow-up interval');
    }
    set({ isUpdating: false });
  },

  checkGoogleStatus: async () => {
    try {
      const { connected, email } = await api.getGoogleStatus();
      set({ googleConnected: connected, googleEmail: connected ? (email || null) : null });
      if (connected) {
        // Fetch the "X of Y synced" counts for the status line.
        try { set({ googleSyncCounts: await api.getGoogleSyncCounts() }); } catch { /* non-critical */ }
      } else {
        set({ googleSyncCounts: null });
      }
    } catch (err) {
      console.warn('[Settings] checkGoogleStatus failed:', err);
      set({ googleConnected: get().googleConnected });
    }
  },

  getGoogleAuthUrl: async () => {
    const { url } = await api.getGoogleAuthUrl();
    return url;
  },

  disconnectGoogle: async (events: 'keep' | 'remove' = 'keep') => {
    set({ isUpdating: true });
    try {
      await api.disconnectGoogle(events);
      set({ googleConnected: false, googleEmail: null });
    } catch (err) {
      console.warn('[Settings] disconnectGoogle failed:', err);
      Alert.alert(i18n.t('auth.error'), i18n.t('settings.googleDisconnectFailed', 'Failed to disconnect Google Calendar'));
    }
    set({ isUpdating: false });
  },

  syncAllGoogle: async () => {
    set({ isUpdating: true });
    let synced = 0;
    try {
      const r = await api.syncAllGoogle();
      synced = r?.synced ?? 0;
    } catch (err) {
      console.warn('[Settings] syncAllGoogle failed:', err);
      Alert.alert(i18n.t('auth.error'), i18n.t('settings.googleSyncAllFailed', 'Failed to sync reminders'));
    }
    set({ isUpdating: false });
    return synced;
  },

  importGoogleEvents: async () => {
    set({ isUpdating: true });
    let result = { imported: 0, updated: 0 };
    try {
      const r = await api.importGoogleEvents();
      result = { imported: r?.imported ?? 0, updated: r?.updated ?? 0 };
    } catch (err) {
      console.warn('[Settings] importGoogleEvents failed:', err);
      Alert.alert(i18n.t('auth.error'), i18n.t('settings.googleImportFailed', 'Failed to import from Google'));
    }
    set({ isUpdating: false });
    return result;
  },

  refreshGoogleSyncCounts: async () => {
    try {
      const c = await api.getGoogleSyncCounts();
      set({ googleSyncCounts: c });
    } catch (err) {
      console.warn('[Settings] refreshGoogleSyncCounts failed:', err);
    }
  },

  updateDisplayName: async (name: string) => {
    set({ isUpdating: true });
    try {
      const { user } = await api.updateProfile({ displayName: name });
      useAuthStore.getState().updateUser(user);
    } catch (err) {
      console.warn('[Settings] updateDisplayName failed:', err);
      Alert.alert(i18n.t('auth.error'), i18n.t('settings.nameChangeFailed'));
    }
    set({ isUpdating: false });
  },
}));
